import ArrowLeftIcon from "@/components/_icons/arrowLeftIcon";
import { ScheduledDatePicker } from "@/components/scheduledDatePicker/scheduledDatePicker";
import { daysOfWeek } from "@/constants/enum";
import { useScheduledDropdownOptions } from "@/hooks/useScheduledDropdownOptions";
import { FEATURE_FLAG_ADVANCED_ORDERING } from "@/utils/constants";
import { HHMMtoDate } from "@/utils/dateTimeUtils";
import { checkIsHomePage } from "@/utils/nativeBridgeUtils";
import { getBusinessHoursOfDay } from "@/utils/scheduleOrderUtils";
import { getFromSessionStorage } from "@/utils/sessionStorageUtils";
import { SessionStorageKey } from "@/utils/storageKeys";
import { useMerchantFeatureFlag } from "@/utils/useMerchantFeatureFlag";
import { useNativeActions } from "@/utils/useNativeActions";
import { isEmpty } from "lodash";
import {DateTime} from "luxon";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { PrimaryButton } from "../button/primaryButton";
import { ScheduledDateDropdown } from "../scheduledDateDropdown/scheduledDateDropdown";
import { ScheduledTimeDropdown } from "../scheduledTimeDropdown/scheduledTimeDropdown";


export const RestaurantCartScheduledOrderForm = ({
  merchant,
  businessHours,
  handleBackToCart,
  scheduleDate,
  setScheduleDate,
  allDaysEmpty,
  scheduleTime,
  setScheduleTime,
  merchantColor,
  handleConfirmScheduledOrder,
  orderType,
  selectedDayPart = null,
  isOpen,
}: any) => {
  const t = useTranslations();
  const pathName = usePathname();
  const isHomePage = checkIsHomePage(
    pathName,
    merchant.merchantMeta.selectedSetup === "Simple",
    merchant.isStandaloneGiftCardWebsite
  );
  const { leaveHomePage, enterHomePage, isCmaApp } = useNativeActions();
  const orderEstimates =
    getFromSessionStorage(SessionStorageKey.ORDER_ESTIMATES, {}) || {};
  const {
    dateOptions,
    calendarOptions,
    timeOptions,
    isDateDisabled,
    isTimeDisabled,
    onDateChange,
  } = useScheduledDropdownOptions({
    scheduleDateRange: merchant?.scheduledOrdering?.maxDays - 1,
    businessHours,
    selectedDayPart,
    orderType,
    merchant,
    orderEstimates,
    scheduleDate,
    scheduleTime,
    setScheduleDate,
    setScheduleTime,
  });
  const getDayPartMenuEndTime = () => {
    const dayIndex = DateTime.fromJSDate(scheduleDate.dateObject, {
      zone: merchant.timezone,
    }).weekday % 7;
    const dayName = daysOfWeek[dayIndex];
    const businessHoursOfDay = getBusinessHoursOfDay(
      businessHours,
      dayName,
      selectedDayPart
    );
    if (businessHoursOfDay.length > 0) {
      return businessHoursOfDay[0].end;
    }

    return selectedDayPart.time.endTime.toString();
  };
  const useDatePicker = useMerchantFeatureFlag(merchant, FEATURE_FLAG_ADVANCED_ORDERING);

  useEffect(() => {
    if (!isHomePage || !isCmaApp) return;

    if (isOpen) {
      leaveHomePage();
    }

    return () => {
      if (isOpen) {
        enterHomePage();
      }
    };
  }, [isOpen, isHomePage]);

  return (
    <div className="flex flex-col h-full p-4">
      <div className="pt-4 flex flex-row gap-2">
        <button
          onClick={() => {
            handleBackToCart();
          }}
          className="text-gray-600 hover:text-gray-900"
          aria-label={t("checkout.backToCart")}
        >
          <ArrowLeftIcon />
        </button>
        <h2 className="text-xl font-bold">{t("checkout.scheduleYourOrder")}</h2>
      </div>
      {dateOptions.length > 0 ? (
        <>
          {!isEmpty(selectedDayPart) && (
            <div className="flex flex-row justify-center items-center bg-[#FFF6CC] shadow-md p-2 rounded-md my-4 border-[1px] boder-solid border-[#FFD100]">
              <p>
                {t(
                  "RestaurantCartScheduledOrderForm.daypartScheduleOrderMessage",
                  {
                    dayPartName: selectedDayPart.menuName ?? "",
                    dayPartEndTime:
                      HHMMtoDate(
                        getDayPartMenuEndTime().padStart(4, "0"),
                        merchant.timezone,
                        new Date()
                      )?.toLocaleString({
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      }) ?? "",
                  }
                )}
              </p>
            </div>
          )}
          <div className="py-4">
            {useDatePicker ? (
              <ScheduledDatePicker
                scheduleDate={scheduleDate}
                calendarOptions={calendarOptions}
                onDateChange={onDateChange}
                timezone={merchant.timezone}
                isDisabled={isDateDisabled}
              />
            ) : (
              <ScheduledDateDropdown
                scheduleDate={scheduleDate}
                dateOptions={dateOptions}
                onDateChange={onDateChange}
                isDisabled={isDateDisabled}
              />
            )}
          </div>
          {businessHours && !allDaysEmpty && (
            <div className="py-4">
              <ScheduledTimeDropdown
                scheduleTime={scheduleTime}
                setScheduleTime={setScheduleTime}
                timezone={merchant.timezone}
                timeOptions={timeOptions}
                isDisabled={isTimeDisabled}
              />
            </div>
          )}
          <div className="mt-auto pb-4 flex gap-4">
            <PrimaryButton
              style={{
                backgroundColor:
                  scheduleTime.displayValue == "" ? "#C7C7C7" : merchantColor,
              }}
              className="bg-black text-white font-bold py-2 rounded-md w-full h-14 disabled:bg-[#C7C7C7]"
              onClick={handleConfirmScheduledOrder}
              disabled={scheduleTime.displayValue == ""}
              aria-label={t("RetailCartDrawer.confirm")}
            >
              <p className="text-xl font-medium">
                {t("RetailCartDrawer.confirm")}
              </p>
            </PrimaryButton>
          </div>
        </>
      ) : (
        <p className="pt-4">{t("homePage.noScheduleMsg")}</p>
      )}
    </div>
  );
};
